<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { usePlayground } from './usePlayground';

// D35/spec: "Playground = persistent slide-over" — chat against the CURRENT
// DRAFT with a tool-call inspector, reachable from every agent-detail tab.
// D19: only display_name renders, never the sentinel email.
const props = defineProps({
  brain: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const {
  isOpen,
  isWaiting,
  messages,
  rendered,
  error,
  sendMessage,
  reset,
  close,
} = usePlayground(props.brain.id);

const input = ref('');
const expandedToolCalls = ref({});
const messageListRef = ref(null);

const scrollToBottom = async () => {
  await nextTick();
  const el = messageListRef.value;
  if (el) el.scrollTop = el.scrollHeight;
};

watch(() => messages.value.length, scrollToBottom);

const send = async () => {
  const text = input.value;
  input.value = '';
  await sendMessage(text);
};

const toolCallKey = (message, index) => `${message.id}-${index}`;

const toggleToolCall = (message, index) => {
  const key = toolCallKey(message, index);
  expandedToolCalls.value[key] = !expandedToolCalls.value[key];
};

const formatToolInput = toolCall =>
  JSON.stringify(toolCall.input || {}, null, 2);

const errorText = computed(() => {
  if (!error.value) return '';
  if (error.value.type === 'server') return error.value.message;
  return t('AI_AGENTS.PLAYGROUND.ERRORS.RUNTIME_UNAVAILABLE');
});

const errorDetail = computed(() =>
  error.value?.type === 'unreachable' && error.value.message
    ? t('AI_AGENTS.PLAYGROUND.ERRORS.DETAIL', { error: error.value.message })
    : ''
);
</script>

<template>
  <aside
    v-if="isOpen"
    class="fixed inset-y-0 ltr:right-0 rtl:left-0 z-50 flex flex-col w-[26rem] max-w-full bg-n-solid-1 ltr:border-l rtl:border-r border-n-weak shadow-xl"
  >
    <header
      class="flex items-center gap-2 px-4 py-3 border-b border-n-weak"
    >
      <div class="flex flex-col min-w-0">
        <span class="text-base font-medium truncate text-n-slate-12">
          {{ t('AI_AGENTS.PLAYGROUND.TITLE') }} — {{ brain.display_name }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ t('AI_AGENTS.PLAYGROUND.DRAFT_NOTE') }}
        </span>
      </div>
      <div class="ltr:ml-auto rtl:mr-auto flex items-center gap-1">
        <Button
          v-tooltip="t('AI_AGENTS.PLAYGROUND.RESET')"
          icon="i-lucide-rotate-ccw"
          variant="ghost"
          color="slate"
          size="sm"
          @click="reset"
        />
        <Button
          v-tooltip="t('AI_AGENTS.PLAYGROUND.CLOSE')"
          icon="i-lucide-x"
          variant="ghost"
          color="slate"
          size="sm"
          @click="close"
        />
      </div>
    </header>

    <div
      ref="messageListRef"
      class="flex flex-col flex-1 gap-3 px-4 py-3 overflow-y-auto"
    >
      <p
        v-if="!messages.length"
        class="py-8 text-sm text-center text-n-slate-11"
      >
        {{ t('AI_AGENTS.PLAYGROUND.EMPTY') }}
      </p>

      <div
        v-for="message in messages"
        :key="message.id"
        class="flex flex-col gap-1"
        :class="message.role === 'user' ? 'items-end' : 'items-start'"
      >
        <div
          class="max-w-[85%] px-3 py-2 text-sm rounded-xl whitespace-pre-wrap"
          :class="
            message.role === 'user'
              ? 'bg-n-blue-9 text-white'
              : 'bg-n-solid-3 text-n-slate-12'
          "
        >
          {{ message.content }}
        </div>
        <span v-if="message.failed" class="text-xs text-n-ruby-11">
          {{ t('AI_AGENTS.PLAYGROUND.MESSAGE_FAILED') }}
        </span>

        <div
          v-if="message.toolCalls?.length"
          class="flex flex-col gap-1 max-w-[85%]"
        >
          <span class="text-xs text-n-slate-10">
            {{ t('AI_AGENTS.PLAYGROUND.TOOL_CALLS.TITLE') }} ·
            {{ t('AI_AGENTS.PLAYGROUND.TOOL_CALLS.NOT_EXECUTED') }}
          </span>
          <div
            v-for="(toolCall, index) in message.toolCalls"
            :key="toolCallKey(message, index)"
            class="flex flex-col rounded-lg outline outline-1 outline-n-weak"
          >
            <button
              class="flex items-center gap-2 px-2 py-1.5 text-xs font-medium text-n-slate-12"
              @click="toggleToolCall(message, index)"
            >
              <span class="i-lucide-wrench size-3.5 flex-shrink-0" />
              {{ toolCall.name }}
              <span
                class="ltr:ml-auto rtl:mr-auto size-3.5"
                :class="
                  expandedToolCalls[toolCallKey(message, index)]
                    ? 'i-lucide-chevron-up'
                    : 'i-lucide-chevron-down'
                "
              />
            </button>
            <pre
              v-if="expandedToolCalls[toolCallKey(message, index)]"
              class="px-2 py-1.5 overflow-x-auto text-xs border-t border-n-weak bg-n-solid-3 text-n-slate-12"
              >{{ formatToolInput(toolCall) }}</pre
            >
          </div>
        </div>
      </div>

      <div
        v-if="isWaiting"
        class="flex items-center gap-2 text-sm text-n-slate-11"
      >
        <Spinner />
        {{ t('AI_AGENTS.PLAYGROUND.WAITING') }}
      </div>
    </div>

    <div
      v-if="errorText"
      class="flex flex-col gap-0.5 px-4 py-2 text-sm bg-n-ruby-3 text-n-ruby-11"
    >
      <span>{{ errorText }}</span>
      <span v-if="errorDetail" class="text-xs">{{ errorDetail }}</span>
    </div>

    <form
      class="flex items-end gap-2 px-4 py-3 border-t border-n-weak"
      @submit.prevent="send"
    >
      <textarea
        v-model="input"
        rows="2"
        :placeholder="t('AI_AGENTS.PLAYGROUND.INPUT_PLACEHOLDER')"
        class="flex-1 px-3 py-2 text-sm rounded-lg resize-none outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
        @keydown.enter.exact.prevent="send"
      />
      <Button
        type="submit"
        :label="t('AI_AGENTS.PLAYGROUND.SEND')"
        size="sm"
        :is-loading="isWaiting"
        :disabled="isWaiting || !input.trim()"
      />
    </form>

    <footer
      v-if="rendered"
      class="px-4 py-1.5 text-xs border-t border-n-weak text-n-slate-10"
    >
      {{ t('AI_AGENTS.PLAYGROUND.META.MODEL', { model: rendered.model }) }} ·
      {{ t('AI_AGENTS.PLAYGROUND.META.TOOLS', { count: rendered.tool_count }) }}
    </footer>
  </aside>
</template>
